import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// List of public routes that are safe for tracking (no PHI)
// Reusing the same safe routes as GoogleTagManager
const safeRoutes = ['/', '/signin', '/register', '/resetpassword', '/terms', '/privacy', '/cookies', '/data-protection', '/ai-ethics'];

declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

// Helper functions for tracking specific events
export const trackViewContent = (contentData?: { content_name?: string; content_type?: string; content_ids?: string[] }) => {
  if (!window.fbq) {
    console.warn('Facebook Pixel not loaded, cannot track ViewContent');
    return false;
  }
  
  console.log('Tracking ViewContent with Facebook Pixel');
  window.fbq('track', 'ViewContent', contentData || {});
  return true;
};

export const trackContact = (contactData?: { content_category?: string; content_name?: string }) => {
  if (!window.fbq) {
    console.warn('Facebook Pixel not loaded, cannot track Contact');
    return false;
  }
  
  console.log('Tracking Contact with Facebook Pixel');
  window.fbq('track', 'Contact', contactData || {});
  return true;
};

export const trackCompleteRegistration = (registrationData?: { 
  content_name?: string; 
  status?: string;
  value?: number;
  currency?: string;
}) => {
  if (!window.fbq) {
    console.warn('Facebook Pixel not loaded, cannot track CompleteRegistration');
    return false;
  }
  
  console.log('Tracking CompleteRegistration with Facebook Pixel');
  window.fbq('track', 'CompleteRegistration', registrationData || {});
  return true;
};

export const trackSubscribe = (subscribeData?: { 
  value?: number;
  currency?: string;
  predicted_ltv?: number;
}) => {
  if (!window.fbq) {
    console.warn('Facebook Pixel not loaded, cannot track Subscribe');
    return false;
  }
  
  console.log('Tracking Subscribe with Facebook Pixel');
  window.fbq('track', 'Subscribe', subscribeData || {});
  return true;
};

export const trackStartTrial = (trialData?: { 
  value?: number;
  currency?: string;
  predicted_ltv?: number;
}) => {
  if (!window.fbq) {
    console.warn('Facebook Pixel not loaded, cannot track StartTrial');
    return false;
  }
  
  console.log('Tracking StartTrial with Facebook Pixel');
  window.fbq('track', 'StartTrial', trialData || {});
  return true;
};

export const FacebookPixel = (): null => {
  const location = useLocation();
  
  useEffect(() => {
    // Only load Facebook Pixel on safe routes
    const currentPath = location.pathname;
    const isSafeRoute = safeRoutes.includes(currentPath) || 
                        currentPath.startsWith('/setpassword/');
    
    if (isSafeRoute) {
      // Initialize Facebook Pixel if it doesn't exist
      if (!window.fbq) {
        // Initialize fbq function - using eval to avoid TypeScript errors with the Facebook code
        // This is the standard Facebook pixel initialization code
        const pixelCode = `
          !function(f,b,e,v,n,t,s) {
            if(f.fbq) return; n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s);
          }(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
        `;
        // Using Function constructor instead of eval for better security
        new Function(pixelCode)();
        
        // Initialize pixel with your ID
        window.fbq('init', '712267611136632');
        
        // Track PageView
        window.fbq('track', 'PageView');
        
        // Add noscript pixel
        if (!document.getElementById('fb-pixel-noscript')) {
          const noscript = document.createElement('noscript');
          noscript.id = 'fb-pixel-noscript';
          
          const img = document.createElement('img');
          img.height = 1;
          img.width = 1;
          img.style.display = 'none';
          img.src = 'https://www.facebook.com/tr?id=712267611136632&ev=PageView&noscript=1';
          
          noscript.appendChild(img);
          document.body.appendChild(noscript);
        }
        
        console.log('Facebook Pixel initialized on route:', currentPath);
      } else {
        // If pixel is already initialized, just track page view
        window.fbq('track', 'PageView');
      }
    } else {
      // Clear pixel for protected routes
      // Note: We can't actually remove the Facebook Pixel once loaded,
      // but we can avoid making further tracking calls
      console.log('Facebook Pixel inactive on protected route:', currentPath);
    }
  }, [location.pathname]);
  
  return null;
};

export default FacebookPixel;